import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import CRMLayout from './CRMLayout';
import { toast } from 'sonner';
import {
  Plus, Search, Users, User, Mail, Phone, Shield, 
  Edit, Trash2, Loader2, UserCheck, UserX, BarChart3, TrendingUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const ROLES = ['employee', 'freelancer', 'admin', 'super_admin'];

export default function CRMTeam() {
  const { token, isAdmin, user: currentUser } = useCRMAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'employee', phone: '', designation: '', hourly_rate: '' });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API}/api/crm/users`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setUsers((await res.json()).users || []);
    } catch (err) {} 
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name || !form.email) { toast.error('Name and email required'); return; }
    if (!editing && !form.password) { toast.error('Password required for new user'); return; }
    setSaving(true);
    try {
      const method = editing ? 'PUT' : 'POST';
      const url = editing ? `${API}/api/crm/users/${editing.id}` : `${API}/api/crm/users`;
      const body = { ...form, hourly_rate: parseFloat(form.hourly_rate) || 0 };
      if (editing && !form.password) delete body.password;
      
      const res = await fetch(url, {
        method, headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        toast.success(editing ? 'User updated!' : 'User created!');
        setShowModal(false); setEditing(null);
        setForm({ name: '', email: '', password: '', role: 'employee', phone: '', designation: '', hourly_rate: '' });
        fetchUsers();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch (err) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleToggleStatus = async (userId, isActive) => {
    try {
      const res = await fetch(`${API}/api/crm/users/${userId}/status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !isActive })
      });
      if (res.ok) { toast.success(isActive ? 'User deactivated' : 'User activated'); fetchUsers(); }
    } catch (err) { toast.error('Failed to update status'); }
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, password: '', role: u.role, phone: u.phone || '', designation: u.designation || '', hourly_rate: u.hourly_rate?.toString() || '' });
    setShowModal(true);
  };

  const filtered = users.filter(u => 
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.role?.toLowerCase().includes(search.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const colors = { super_admin: 'bg-purple-100 text-purple-800', admin: 'bg-blue-100 text-blue-800', employee: 'bg-green-100 text-green-800', freelancer: 'bg-amber-100 text-amber-800' };
    return <Badge className={colors[role] || 'bg-slate-100'}>{role?.replace('_', ' ')}</Badge>;
  };

  return (
    <CRMLayout>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Team Management</h1>
          <p className="text-slate-500">Manage employees and freelancers</p>
        </div>
        {isAdmin() && (
          <Button onClick={() => { setEditing(null); setForm({ name: '', email: '', password: '', role: 'employee', phone: '', designation: '', hourly_rate: '' }); setShowModal(true); }} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" /> Add Member
          </Button>
        )}
      </div>

      <Card className="p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search team members..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
        </div>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No team members found</h3>
          <p className="text-slate-500">Add your first team member to get started</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(u => (
            <Card key={u.id} className={`p-5 ${!u.is_active ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg ${u.is_active ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : 'bg-slate-400'}`}>
                    {u.name?.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{u.name}</h3>
                    <p className="text-sm text-slate-500">{u.designation || u.role}</p>
                  </div>
                </div>
                {getRoleBadge(u.role)}
              </div>
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Mail className="w-4 h-4" /><span className="truncate">{u.email}</span>
                </div>
                {u.phone && <div className="flex items-center gap-2 text-sm text-slate-600"><Phone className="w-4 h-4" /><span>{u.phone}</span></div>}
                {u.hourly_rate > 0 && <div className="flex items-center gap-2 text-sm text-slate-600"><BarChart3 className="w-4 h-4" /><span>₹{u.hourly_rate}/hr</span></div>}
              </div>
              {isAdmin() && u.id !== currentUser?.id && (
                <div className="flex items-center gap-2 pt-4 border-t">
                  <Link to={`/crm/performance/${u.id}`} className="flex-1">
                    <Button variant="outline" size="sm" className="w-full"><TrendingUp className="w-4 h-4 mr-1" /> Performance</Button>
                  </Link>
                  <Button variant="outline" size="sm" onClick={() => openEdit(u)}><Edit className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => handleToggleStatus(u.id, u.is_active)} className={u.is_active ? 'text-amber-600' : 'text-green-600'}>
                    {u.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Edit Member' : 'Add Member'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Name *</label>
              <Input placeholder="John Doe" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Email *</label>
              <Input type="email" placeholder="john@agency.com" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Password {editing ? '(leave blank to keep)' : '*'}</label>
              <Input type="password" placeholder="••••••••" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Role</label>
                <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Hourly Rate (₹)</label>
                <Input type="number" placeholder="500" value={form.hourly_rate} onChange={(e) => setForm({...form, hourly_rate: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Designation</label>
              <Input placeholder="Graphic Designer" value={form.designation} onChange={(e) => setForm({...form, designation: e.target.value})} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Phone</label>
              <Input placeholder="+91 9876543210" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? 'Update' : 'Create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CRMLayout>
  );
}
